import { Badge } from '@/components/ui/badge'
import { isSubscriptionPackageCollection } from '@/lib/subscription-package-collection'
import { cn } from '@/lib/utils'
import { LockKeyhole, Sparkles } from 'lucide-react'

export function getCollectionMembershipMode(collection) {
  if (isSubscriptionPackageCollection(collection)) {
    return 'automatic'
  }

  return collection?.collection_membership_type === 'smart' ? 'automatic' : 'manual'
}

export function getCollectionOriginBadgeMeta(collection) {
  if (collection?.collection_origin === 'platform') {
    return {
      icon: LockKeyhole,
      label: 'Built-in',
      variant: 'brand',
    }
  }

  return {
    label: 'Custom',
    variant: 'outline',
  }
}

export function getCollectionOriginBadgeToneClassName(collection) {
  if (collection?.collection_origin === 'platform') {
    return 'border-primary/25 bg-primary/10 text-interaction shadow-none'
  }

  return 'border-border/70 bg-background/40 text-muted-foreground shadow-none'
}

export function getCollectionMembershipBadgeMeta({
  collection,
  automaticLabel = 'Automatic',
  membershipMode,
  manualLabel = 'Manual',
} = {}) {
  const mode = membershipMode ?? getCollectionMembershipMode(collection)

  if (mode === 'automatic') {
    return {
      icon: Sparkles,
      label: automaticLabel,
      variant: 'info',
    }
  }

  return {
    label: manualLabel,
    variant: 'outline',
  }
}

export function getCollectionMembershipBadgeToneClassName({ collection, membershipMode } = {}) {
  if ((membershipMode ?? getCollectionMembershipMode(collection)) === 'automatic') {
    return 'border-selection-border/45 bg-accentSecondary text-accentSecondary-foreground shadow-none'
  }

  return 'border-border/70 bg-background/40 text-muted-foreground shadow-none'
}

function ExplainedBadge({ meta, prefix, size = 'xs', className }) {
  const label = prefix ? `${prefix}: ${meta.label}` : meta.label
  const Icon = meta.icon

  return (
    <span data-index-table-drag-scroll-ignore className="inline-flex">
      <Badge
        variant={meta.variant}
        size={size}
        className={cn('cursor-default', Icon && 'gap-1.5 [&>svg]:size-3', className)}
      >
        {Icon ? <Icon aria-hidden="true" /> : null}
        {label}
      </Badge>
    </span>
  )
}

export function CollectionOriginBadge({ collection, prefix, size, className }) {
  return (
    <ExplainedBadge
      meta={getCollectionOriginBadgeMeta(collection)}
      prefix={prefix}
      size={size}
      className={className}
    />
  )
}

export function CollectionMembershipBadge({
  collection,
  automaticLabel,
  membershipMode,
  manualLabel,
  prefix,
  size,
  className,
}) {
  return (
    <ExplainedBadge
      meta={getCollectionMembershipBadgeMeta({
        collection,
        automaticLabel,
        membershipMode,
        manualLabel,
      })}
      prefix={prefix}
      size={size}
      className={className}
    />
  )
}
